import { ExecutionResult } from '@lcs/shared/api';
import {
   BaseStationHandsetInput,
   ExtensionAutoAttendantInput,
   ExtensionExternalForwardInput,
   ExtensionFaxInput,
   ExtensionFmFmInput,
   ExtensionHuntGroupInput,
   ExtensionInput,
   ExtensionMailboxInput,
   ExtensionPagingGroupInput,
   ExtensionPhoneInput,
   ExtensionQueueInput,
   ExtensionTypes,
   FaxEmailAddressInput,
   MailboxInput,
   NoteInput,
   NullActionTypes,
   TimeBasedRouteInput,
   UserInput,
} from 'generated/graphql-types';
import { PhoneDetailSaveModel } from 'src/app/pages/add-phone/models/phone-detail-save.model';
import {
   DepartmentExtensionItemView,
   departmentExtensionKeyFields,
   departmentKeyFields,
} from 'src/app/pages/extension-details/models/department-extension.model';
import {
   ExtensionDetailSaveModel,
   ExtensionGroupHuntGroupSaveModel,
   ExtensionGroupSuperviseeSaveModel,
   ExtensionSuperviseeSaveModel,
   ExtensionUserSaveModel,
   FindMeFollowMeStepExtensionSaveModel,
   FindMeFollowMeStepPhoneNumberSaveModel,
   FindMeFollowMeStepSaveModel,
   HuntGroupMemberSaveModel,
   PagingGroupExtensionGroupSaveModel,
   PagingGroupMemberSaveModel,
   QueueGroupMemberSaveModel,
   QueueMemberSaveModel,
} from 'src/app/pages/extension-details/models/extension-detail-save.model';
import {
   extensionGroupSuperviseeKeyFields,
   extensionSuperviseeKeyFields,
} from 'src/app/pages/extension-details/models/extension-supervisee-item.model';
import { extensionUserKeyFields } from 'src/app/pages/extension-details/models/extension-user-item.model';
import { huntGroupMemberKeyFields } from 'src/app/pages/extension-details/models/hunt-group-member-item.model';
import { pagingGroupMemberKeyFields } from 'src/app/pages/extension-details/models/paging-group-member-item.model';
import {
   baseStationFields,
   deviceFields,
   phoneFields,
   sipUserFields,
} from 'src/app/pages/extension-details/models/phone-item.model';
import { queueMemberKeyFields } from 'src/app/pages/extension-details/models/queue-member-item.model';
import {
   GetExtensionDetail_extension_extensionHuntGroup_extensionGroupHuntGroupMembersConnection_items,
   GetExtensionDetail_extension_extensionHuntGroup_huntGroupMembersConnection_items,
   GetExtensionDetail_extension_extensionPagingGroup_pagingGroupExtensionGroupsConnection_items,
   GetExtensionDetail_extension_extensionPagingGroup_pagingGroupMembersConnection_items,
   GetExtensionDetail_extension_extensionPhone_extensionGroupSuperviseesConnection_items,
   GetExtensionDetail_extension_extensionPhone_extensionSuperviseesConnection_items,
   GetExtensionDetail_extension_extensionPhone_phonesConnection_items,
   GetExtensionDetail_extension_extensionQueue_queueExtensionGroupMembersConnection_items,
   GetExtensionDetail_extension_extensionQueue_queueMembersConnection_items,
   GetExtensionDetail_extension_usersConnection_items,
} from 'src/app/pages/extension-details/queries/generated/GetExtensionDetail';
import { SaveExtension } from 'src/app/pages/extension-details/queries/generated/SaveExtension';
import {
   FaxEmailItemView,
   faxEmailAddressesFields,
} from 'src/app/pages/extension-details/views/extension-fax-sections/fax-emails-tab/models/fax-emails-item-view';
import {
   findMeFollowMeStepExtensionsKeyFields,
   findMeFollowMeStepKeyFields,
   findMeFollowMeStepPhoneNumbersKeyFields,
} from 'src/app/pages/extension-details/views/extension-find-me-follow-me-sections/models/extension-find-me-follow-me-step.model';
import {
   TimeBasedRouteItemView,
   timeBasedRoutesFields,
} from 'src/app/pages/extension-time-based-routes/models/extension-time-based-routes.model';
import {
   AutoAttendantKeyItemView,
   autoAttendantKeyFields,
} from '../auto-attendant-key-list/models/auto-attendant-key-item.model';
import {
   AutoAttendantPromptItemView,
   autoAttendantPromptFields,
} from '../auto-attendant-prompt-list/models/auto-attendant-prompt-item.model';
import {
   QueuePeriodicAnnouncementItemView,
   queuePeriodicAnnouncementFields,
} from '../queue-periodic-announcement-list/models/queue-periodic-announcement-item.model';

export function createExtensionInput(
   extensionSave: ExtensionDetailSaveModel,
   extensionFields: (keyof ExtensionInput)[],
   extensionPhoneFields: (keyof ExtensionPhoneInput)[] | null,
   extensionPagingGroupFields: (keyof ExtensionPagingGroupInput)[] | null,
   extensionExternalForwardFields: (keyof ExtensionExternalForwardInput)[] | null,
   extensionAutoAttendantFields: (keyof ExtensionAutoAttendantInput)[] | null,
   extensionHuntGroupFields: (keyof ExtensionHuntGroupInput)[] | null,
   extensionMailboxFields: (keyof ExtensionMailboxInput)[] | null,
   mailboxFields: (keyof MailboxInput)[] | null,
   extensionQueueFields: (keyof ExtensionQueueInput)[] | null,
   extensionFaxFields: (keyof ExtensionFaxInput)[] | null,
   extensionFindMeFollowMeFields: (keyof ExtensionFmFmInput)[] | null,
   isSavingNewDepartments: boolean
): ExtensionInput {
   extensionFields = [...extensionFields, 'extensionId'];
   if (extensionPhoneFields) {
      extensionPhoneFields = [...extensionPhoneFields, 'extensionId', 'extensionPhoneId'];
   }
   if (extensionPagingGroupFields) {
      extensionPagingGroupFields = [...extensionPagingGroupFields, 'extensionId', 'extensionPagingGroupId'];
   }

   if (extensionAutoAttendantFields) {
      extensionAutoAttendantFields = [...extensionAutoAttendantFields, 'extensionId', 'extensionAutoAttendantId'];
   }
   if (extensionExternalForwardFields) {
      extensionExternalForwardFields = [...extensionExternalForwardFields, 'extensionId', 'extensionExternalForwardId'];
   }
   if (extensionHuntGroupFields) {
      extensionHuntGroupFields = [...extensionHuntGroupFields, 'extensionId', 'extensionHuntGroupId'];
   }
   const timeBasedRouteFieldIndex = extensionFields.indexOf('timeBasedRoutes');
   if (timeBasedRouteFieldIndex !== -1) {
      extensionFields.splice(timeBasedRouteFieldIndex, 1);
      formatTimeBasedRouteFields(extensionFields, 'timeBasedRoutes', timeBasedRoutesFields);
   }
   const extensionUserFieldIndex = extensionFields.indexOf('users');
   if (extensionUserFieldIndex !== -1) {
      extensionFields.splice(extensionUserFieldIndex, 1);
      formatEmbeddedUserFields(extensionFields, 'users');
   }
   const extensionFaxEmailAddressesFieldIndex = extensionFields.indexOf('faxEmailAddresses');
   if (extensionFaxEmailAddressesFieldIndex !== -1) {
      extensionFields.splice(extensionFaxEmailAddressesFieldIndex, 1);
      formatEmbeddedFaxEmailAddressFields(extensionFields, 'faxEmailAddresses');
   }
   const departmentExtensionsFieldIndex = extensionFields.indexOf('departmentExtensions');
   if (departmentExtensionsFieldIndex !== -1) {
      extensionFields.splice(departmentExtensionsFieldIndex, 1);
      formatEmbeddedDepartmentExtensionFields(extensionFields, 'departmentExtensions', isSavingNewDepartments);
   }
   if (extensionMailboxFields) {
      extensionMailboxFields = [...extensionMailboxFields, 'extensionId', 'extensionMailboxId'];
   }
   if (mailboxFields && mailboxFields.length > 0) {
      mailboxFields = [...mailboxFields, 'mailboxId'];
   }
   if (extensionQueueFields) {
      extensionQueueFields = [...extensionQueueFields, 'extensionQueueId', 'extensionId'];
   }
   if (extensionFaxFields) {
      extensionFaxFields = [...extensionFaxFields, 'extensionFaxId', 'extensionId'];
   }
   if (extensionFindMeFollowMeFields) {
      extensionFindMeFollowMeFields = [...extensionFindMeFollowMeFields, 'extensionFmFmId', 'extensionId'];
   }
   const extensionInput: ExtensionInput = {
      extensionId: extensionSave.extension.extensionId,
      adminNote: extensionSave.extension.adminNote,
      extensionNote: extensionSave.extension.extensionNote,
      fields: extensionFields,
      mailboxId: extensionSave.extension.mailboxId,
      name: extensionSave.extension.name,
      shortName: extensionSave.extension.shortName,
      number: extensionSave.extension.number,
      timeBasedRoutes: extensionSave.extension.timeBasedRoutes
         ?.filter((key) => {
            return !key.isDeleted;
         })
         .map((route) => {
            return {
               comment: route.comment,
               endDate: route.endDate,
               endTime: route.endTime,
               extensionId: extensionSave.extension.extensionId,
               fields: [],
               isActiveFriday: route.isActiveFriday,
               isActiveMonday: route.isActiveMonday,
               isActiveSaturday: route.isActiveSaturday,
               isActiveSunday: route.isActiveSunday,
               isActiveThursday: route.isActiveThursday,
               isActiveTuesday: route.isActiveTuesday,
               isActiveWednesday: route.isActiveWednesday,
               priorityId: route.priorityId,
               routeExtensionId: route?.routeExtension?.extensionId ?? null,
               startDate: route.startDate,
               startTime: route.startTime,
               timeBasedRouteId: route.timeBasedRouteId,
               routeExtensionNullAction: route.routeExtensionNullAction,
            };
         }),
      timeZoneId: extensionSave.extension.timeZoneId,
      type: extensionSave.extension.type,
      users: extensionSave.extension.users?.map((user) => {
         return {
            extensionId: user.extensionId,
            fields: [],
            userId: user.userId,
         };
      }),
      faxEmailAddresses: extensionSave.extension.faxEmailAddresses
         ?.filter((key) => {
            return !key.isDeleted;
         })
         .map((key) => {
            return {
               faxEmailAddressId: key.faxEmailAddressId,
               emailAddress: key.emailAddress,
               extensionId: key.extensionId,
               isDomainOnly: /^@[a-zA-Z0-9.-]+\.[a-z]{2,}$/i.test(key.emailAddress),
               fields: [],
            };
         }),
      departmentExtensions: extensionSave.extension.departmentExtensions
         ?.filter((key) => {
            return !key.isDeleted;
         })
         .map((key) => {
            return {
               departmentExtensionId: key.departmentExtensionId,
               extensionId: key.extensionId,
               departmentId: key.department?.departmentId,
               fields: [],
               department: {
                  departmentId: key.department?.departmentId,
                  name: key.department?.name,
                  isActive: key.department?.isActive,
                  accountId: key.department?.accountId,
                  fields: [],
               },
            };
         }),
   };

   extensionFields.forEach((field) => {
      extensionInput[field.toString()] = extensionSave.extension[field];
   });

   if (extensionFields.includes('adminNote')) {
      const noteFields: string[] = ['noteId', 'accountId', 'extensionId', 'notes', 'isSystemNote'];
      if (extensionInput.adminNote?.notes)
         extensionInput.fields.push(
            ...noteFields.map((keyField) => {
               return `adminNote.${keyField}`;
            })
         );
      const index = extensionFields.indexOf('adminNote');
      if (index !== -1) {
         extensionFields.splice(index, 1);
      }
   }

   if (extensionFields.includes('extensionNote')) {
      const noteFields: string[] = ['noteId', 'accountId', 'extensionId', 'notes', 'isSystemNote'];
      if (extensionInput.extensionNote?.notes)
         extensionInput.fields.push(
            ...noteFields.map((keyField) => {
               return `extensionNote.${keyField}`;
            })
         );
      const index = extensionFields.indexOf('extensionNote');
      if (index !== -1) {
         extensionFields.splice(index, 1);
      }
   }
   switch (extensionSave.extension.type) {
      case ExtensionTypes.Phone:
         if (extensionPhoneFields) {
            extensionInput['extensionPhone'] = createExtensionPhoneInput(extensionSave);
            formatExtensionEmbeddedPhoneFields(extensionInput.fields, 'extensionPhone', extensionPhoneFields);
         }
         if (mailboxFields) {
            extensionInput['mailbox'] = createMailboxInput(extensionSave);
            formatEmbeddedMailboxFields(extensionInput.fields, 'mailbox', mailboxFields);
         }
         break;
      case ExtensionTypes.AutoAttendant:
         if (extensionAutoAttendantFields) {
            extensionInput['extensionAutoAttendant'] = createExtensionAutoAttendantInput(extensionSave);
            //Need to do this on add because we mark the form to pristine on default which
            // removes the fields for timeout Extension
            if (extensionSave.extension.extensionAutoAttendant?.useTimeoutToggle === true) {
               extensionAutoAttendantFields = [
                  ...extensionAutoAttendantFields,
                  'timeoutSeconds',
                  'timeoutExtensionId',
                  'timeoutNullAction',
               ];
            }
            formatExtensionEmbeddedAutoAttendantFields(
               extensionInput.fields,
               'extensionAutoAttendant',
               extensionAutoAttendantFields
            );
         }
         break;
      case ExtensionTypes.ExternalForward:
         if (extensionExternalForwardFields) {
            extensionInput['extensionExternalForward'] = createExtensionExternalForwardInput(extensionSave);
            formatExtensionEmbeddedExternalForwardFields(
               extensionInput.fields,
               'extensionExternalForward',
               extensionExternalForwardFields
            );
         }
         break;
      case ExtensionTypes.HuntGroup:
         if (extensionHuntGroupFields) {
            extensionInput['extensionHuntGroup'] = createExtensionHuntGroupInput(extensionSave);
            formatExtensionEmbeddedHuntGroupFields(
               extensionInput.fields,
               'extensionHuntGroup',
               extensionHuntGroupFields
            );
            formatExtensionGroupEmbeddedHuntGroupFields(
               extensionInput.fields,
               'extensionHuntGroup',
               extensionHuntGroupFields
            );
         }
         break;
      case ExtensionTypes.PagingGroup:
         if (extensionPagingGroupFields) {
            extensionInput['extensionPagingGroup'] = createExtensionPagingGroupInput(extensionSave);
            formatExtensionEmbeddedPagingGroupFields(
               extensionInput.fields,
               'extensionPagingGroup',
               extensionPagingGroupFields
            );
            formatExtensionGroupEmbeddedPagingGroupFields(
               extensionInput.fields,
               'extensionPagingGroup',
               extensionPagingGroupFields
            );
         }
         break;
      case ExtensionTypes.SmartVoicemail:
      case ExtensionTypes.VoiceMailbox:
         if (extensionMailboxFields) {
            extensionInput['extensionMailbox'] = createExtensionMailboxInput(extensionSave);
            formatExtensionEmbeddedMailboxFields(extensionInput.fields, 'extensionMailbox', extensionMailboxFields);
         }
         if (mailboxFields) {
            extensionInput['mailbox'] = createMailboxInput(extensionSave);
            formatEmbeddedMailboxFields(extensionInput.fields, 'mailbox', mailboxFields);
         }
         break;
      case ExtensionTypes.CallQueue:
         if (extensionQueueFields) {
            extensionInput['extensionQueue'] = createExtensionQueueInput(extensionSave);
            formatExtensionEmbeddedQueueFields(extensionInput.fields, 'extensionQueue', extensionQueueFields);
            formatExtensionGroupEmbeddedQueueGroupFields(extensionInput.fields, 'extensionQueue', extensionQueueFields);
         }
         break;
      case ExtensionTypes.Fax:
         if (extensionFaxFields) {
            extensionInput['extensionFax'] = createExtensionFaxInput(extensionSave);

            formatExtensionEmbeddedFaxFields(extensionInput.fields, 'extensionFax', extensionFaxFields);

            if (
               extensionInput.extensionFax.faxEmailAddresses &&
               extensionInput.extensionFax.faxEmailAddresses.length > 0
            ) {
               formatExtensionEmbeddedFaxFieldsForFaxEmailAddresses(extensionInput.fields);
            }
         }
         break;
      case ExtensionTypes.FindMeFollowMe:
         if (extensionFindMeFollowMeFields) {
            extensionInput['extensionFmFm'] = createExtensionFmFmInput(extensionSave);

            formatExtensionEmbeddedFindMeFollowMeFields(
               extensionInput.fields,
               'extensionFmFm',
               extensionFindMeFollowMeFields
            );
         }
         break;
      default:
         break;
   }
   return extensionInput;
}

export function getDeletedPhoneIds(
   currentPhones: PhoneDetailSaveModel[],
   existingPhones: GetExtensionDetail_extension_extensionPhone_phonesConnection_items[]
): number[] {
   if (!currentPhones || !existingPhones) {
      return [];
   }
   return existingPhones
      .filter((phone) => {
         return !currentPhones.some((currentPhone) => {
            return currentPhone.phoneId === phone.phoneId;
         });
      })
      .map((deletedPhone) => {
         return deletedPhone.phoneId;
      });
}

export function getDeletedAutoAttendantPromptIds(prompts: AutoAttendantPromptItemView[]): number[] {
   return (
      prompts.reduce((acc, key) => {
         if (key.isDeleted && key.autoAttendantPromptId > 0) {
            acc.push(key.autoAttendantPromptId);
         }
         return acc;
      }, [] as number[]) ?? []
   );
}

export function getDeletedTimeBasedRouteIds(routes: TimeBasedRouteItemView[]): number[] {
   return (
      routes.reduce((acc, key) => {
         if (key.isDeleted && key.timeBasedRouteId > 0) {
            acc.push(key.timeBasedRouteId);
         }
         return acc;
      }, [] as number[]) ?? []
   );
}

export function getDeletedFaxEmailAddressIds(emails: FaxEmailItemView[]): number[] {
   return (
      emails.reduce((acc, key) => {
         if (key.isDeleted && key.faxEmailAddressId && key.faxEmailAddressId > 0) {
            acc.push(key.faxEmailAddressId);
         }
         return acc;
      }, [] as number[]) ?? []
   );
}

export function getDeletedAutoAttendantKeyIds(keys: AutoAttendantKeyItemView[]): number[] {
   return (
      keys.reduce((acc, key) => {
         if (key.isDeleted && key.autoAttendantKeyId > 0) {
            acc.push(key.autoAttendantKeyId);
         }
         return acc;
      }, [] as number[]) ?? []
   );
}

export function getDeletedHuntGroupMemberIds(
   currentHuntGroupMembers: HuntGroupMemberSaveModel[] | null,
   existingHuntGroupMembers: GetExtensionDetail_extension_extensionHuntGroup_huntGroupMembersConnection_items[] | null
): number[] {
   if (!currentHuntGroupMembers || !existingHuntGroupMembers) {
      return [];
   }
   return existingHuntGroupMembers
      .filter((hgMember) => {
         return !currentHuntGroupMembers.some((currentHgMembers) => {
            return currentHgMembers.huntGroupMemberId === hgMember.huntGroupMemberId;
         });
      })
      .map((deletedHgMember) => {
         return deletedHgMember.huntGroupMemberId;
      });
}

export function getDeletedPagingGroupMemberIds(
   currentPagingGroupMembers: PagingGroupMemberSaveModel[] | null,
   existingPagingGroupMembers:
      | GetExtensionDetail_extension_extensionPagingGroup_pagingGroupMembersConnection_items[]
      | null
): number[] {
   if (!currentPagingGroupMembers || !existingPagingGroupMembers) {
      return [];
   }
   return existingPagingGroupMembers
      .filter((pgMember) => {
         return !currentPagingGroupMembers.some((currentPgMember) => {
            return currentPgMember.pagingGroupMemberId === pgMember.pagingGroupMemberId;
         });
      })
      .map((deletedPgMember) => {
         return deletedPgMember.pagingGroupMemberId;
      });
}

export function getDeletedNoteIds(note: NoteInput | null): number | undefined {
   if ((!note || !note.notes) && note?.noteId !== 0) {
      return note?.noteId;
   } else {
      return undefined;
   }
}

export function getDeletedPagingGroupExtensionGroupMemberIds(
   currentPagingGroupMembers: PagingGroupExtensionGroupSaveModel[] | null,
   existingPagingGroups:
      | GetExtensionDetail_extension_extensionPagingGroup_pagingGroupExtensionGroupsConnection_items[]
      | null
): number[] {
   if (!currentPagingGroupMembers || !existingPagingGroups) {
      return [];
   }
   return existingPagingGroups
      .filter((pgGroup) => {
         return !currentPagingGroupMembers.some((currentPgGroup) => {
            return currentPgGroup.pagingGroupExtensionGroupId === pgGroup.pagingGroupExtensionGroupId;
         });
      })
      .map((deletedPgGroup) => {
         return deletedPgGroup.pagingGroupExtensionGroupId;
      });
}

export function getDeletedHuntGroupExtensionGroupMemberIds(
   currentHuntGroupMembers: ExtensionGroupHuntGroupSaveModel[] | null,
   existingHuntGroupMembers:
      | GetExtensionDetail_extension_extensionHuntGroup_extensionGroupHuntGroupMembersConnection_items[]
      | null
): number[] {
   if (!currentHuntGroupMembers || !existingHuntGroupMembers) {
      return [];
   }
   return existingHuntGroupMembers
      .filter((hgMember) => {
         return !currentHuntGroupMembers.some((currentHgMembers) => {
            return currentHgMembers.huntGroupExtensionGroupId === hgMember.huntGroupExtensionGroupId;
         });
      })
      .map((deletedHgMember) => {
         return deletedHgMember.huntGroupExtensionGroupId;
      });
}

export function getDeletedQueueGroupExtensionMemberIds(
   currentQueueGroupMembers: QueueGroupMemberSaveModel[] | null,
   existingQueueGroupMembers:
      | GetExtensionDetail_extension_extensionQueue_queueExtensionGroupMembersConnection_items[]
      | null
): number[] {
   if (!currentQueueGroupMembers || !existingQueueGroupMembers) {
      return [];
   }
   return existingQueueGroupMembers
      .filter((hgMember) => {
         return !currentQueueGroupMembers.some((currentHgMembers) => {
            return currentHgMembers.queueExtensionGroupId === hgMember.queueExtensionGroupId;
         });
      })
      .map((deletedHgMember) => {
         return deletedHgMember.queueExtensionGroupId;
      });
}

export function getDeletedQueueMemberIds(
   currentQueueMembers: QueueMemberSaveModel[] | null,
   existingQueueMembers: GetExtensionDetail_extension_extensionQueue_queueMembersConnection_items[] | null
): number[] {
   if (!currentQueueMembers || !existingQueueMembers) {
      return [];
   }
   return existingQueueMembers
      .filter((hgMember) => {
         return (
            hgMember.queueExtensionGroupId === null &&
            !currentQueueMembers.some((currentQueueMembers) => {
               return currentQueueMembers.queueMemberId === hgMember.queueMemberId;
            })
         );
      })
      .map((deletedHgMember) => {
         return deletedHgMember.queueMemberId;
      });
}

export function getDeletedBaseStationHandsetIds(
   currentBaseStationHandsets: BaseStationHandsetInput[] | null,
   existingPhones: GetExtensionDetail_extension_extensionPhone_phonesConnection_items[] | null,
   deletedPhoneIds: number[]
): number[] {
   if (!currentBaseStationHandsets || !existingPhones) {
      return [];
   }
   const existingBaseStationHandsets = existingPhones
      .map((phone) => phone.baseStationHandsetsConnection?.items ?? [])
      .reduce((acc, handsets) => acc.concat(handsets), [])
      .map((handset) => ({
         baseStationHandsetId: handset.baseStationHandsetId,
         deviceId: handset.device.deviceId,
         phoneId: handset.phoneId,
      }));

   return existingBaseStationHandsets
      .filter((existingHandset) => {
         return !currentBaseStationHandsets.some((currentHandset) => {
            return currentHandset.baseStationHandsetId === existingHandset.baseStationHandsetId;
         });
      })
      .filter((deletedHandset) => !deletedPhoneIds.includes(deletedHandset.phoneId))
      .map((deletedHandset) => deletedHandset.baseStationHandsetId);
}

export function getDeletedExtensionSuperviseeIds(
   currentExtensionSupervisees: ExtensionSuperviseeSaveModel[] | null,
   existingExtensionSupervisees:
      | GetExtensionDetail_extension_extensionPhone_extensionSuperviseesConnection_items[]
      | null
): number[] {
   if (!currentExtensionSupervisees || !existingExtensionSupervisees) {
      return [];
   }
   return existingExtensionSupervisees
      .filter((extensionSupervisee) => {
         return !currentExtensionSupervisees.some((curExtensionSupervisees) => {
            return curExtensionSupervisees.extensionSuperviseeId === extensionSupervisee.extensionSuperviseeId;
         });
      })
      .map((deletedExtensionSupervisee) => {
         return deletedExtensionSupervisee.extensionSuperviseeId;
      });
}

export function getDeletedExtensionGroupSuperviseeIds(
   currentExtensionGroupSupervisees: ExtensionGroupSuperviseeSaveModel[] | null,
   existingExtensionGroupSupervisees:
      | GetExtensionDetail_extension_extensionPhone_extensionGroupSuperviseesConnection_items[]
      | null
): number[] {
   if (!currentExtensionGroupSupervisees || !existingExtensionGroupSupervisees) {
      return [];
   }
   return existingExtensionGroupSupervisees
      .filter((extensionGroupSupervisee) => {
         return !currentExtensionGroupSupervisees.some((curExtensionGroupSupervisees) => {
            return (
               curExtensionGroupSupervisees.extensionGroupSuperviseeId ===
               extensionGroupSupervisee.extensionGroupSuperviseeId
            );
         });
      })
      .map((deletedExtensionGroupSupervisee) => {
         return deletedExtensionGroupSupervisee.extensionGroupSuperviseeId;
      });
}

export function getDeletedQueuePeriodicAnnouncementIds(prompts: QueuePeriodicAnnouncementItemView[] | null): number[] {
   return (
      prompts?.reduce((acc, key) => {
         if (key.isDeleted && key.queuePeriodicAnnouncementId > 0) {
            acc.push(key.queuePeriodicAnnouncementId);
         }
         return acc;
      }, [] as number[]) ?? []
   );
}

export function getDeletedFmFmSteps(steps: FindMeFollowMeStepSaveModel[]): number[] {
   return (
      steps?.reduce((acc, key) => {
         if (key.isDeleted && key.fmFmStepId !== null && key.fmFmStepId > 0) {
            acc.push(key.fmFmStepId);
         }
         return acc;
      }, [] as number[]) ?? []
   );
}

export function getDeletedFmFmStepExtensions(steps: FindMeFollowMeStepSaveModel[]): number[] {
   const stepExtensions = steps
      .filter((step) => step.fmFmStepExtensions !== null)
      .map((step) => step.fmFmStepExtensions as FindMeFollowMeStepExtensionSaveModel[])
      .reduce((acc, key) => {
         return acc.concat(key);
      }, []);
   return (
      stepExtensions.reduce((acc, key) => {
         if (key.isDeleted && key.fmFmStepExtensionId !== null && key.fmFmStepExtensionId > 0) {
            acc.push(key.fmFmStepExtensionId);
         }
         return acc;
      }, [] as number[]) ?? []
   );
}

export function getDeletedFmFmStepPhoneNumbers(steps: FindMeFollowMeStepSaveModel[]): number[] {
   const stepPhoneNumbers = steps
      .filter((step) => step.fmFmStepPhoneNumbers !== null)
      .map((step) => step.fmFmStepPhoneNumbers as FindMeFollowMeStepPhoneNumberSaveModel[])
      .reduce((acc, key) => {
         return acc.concat(key);
      }, []);
   return (
      stepPhoneNumbers.reduce((acc, key) => {
         if (key.isDeleted && key.fmFmStepPhoneNumberId !== null && key.fmFmStepPhoneNumberId > 0) {
            acc.push(key.fmFmStepPhoneNumberId);
         }
         return acc;
      }, [] as number[]) ?? []
   );
}

export function getDeletedDepartmentExtensionIds(departments: DepartmentExtensionItemView[]): number[] {
   return (
      departments.reduce((acc, key) => {
         if (key.isDeleted && key.departmentExtensionId !== null && key.departmentExtensionId > 0) {
            acc.push(key.departmentExtensionId);
         }
         return acc;
      }, [] as number[]) ?? []
   );
}

function createExtensionPhoneInput(extensionSave: ExtensionDetailSaveModel): ExtensionPhoneInput {
   return {
      defaultPhoneNumberId: extensionSave.extension.extensionPhone?.defaultPhoneNumberId,
      defaultAreaCode: extensionSave.extension.extensionPhone?.defaultAreaCode,
      dndStatus: extensionSave.extension.extensionPhone?.dndStatus,
      displayName: extensionSave.extension.extensionPhone?.displayName,
      extensionId: extensionSave.extension.extensionId,
      extensionPhoneId: extensionSave.extension.extensionPhone?.extensionPhoneId,
      fields: [],
      isAutoAnswer: extensionSave.extension.extensionPhone?.isAutoAnswer,
      isRingAllPhones: extensionSave.extension.extensionPhone?.isRingAllPhones,
      autoAnswerPhoneId: extensionSave.extension.extensionPhone?.autoAnswerPhoneId,
      isAutoAnswerEnabled: extensionSave.extension.extensionPhone?.isAutoAnswerEnabled,
      forwardExternalNumber: extensionSave.extension.extensionPhone?.forwardExternalNumber,
      forwardExtensionId: extensionSave.extension.extensionPhone?.forwardToExtensionId,
      office: extensionSave.extension.extensionPhone?.office,
      phones: extensionSave.extension.extensionPhone?.phones?.map((phone) => {
         return {
            devices: phone.devices?.map((device) => {
               return {
                  deviceId: device.deviceId,
                  fields: [],
               };
            }),
            extensionId: phone.extensionId,
            fields: [],
            phoneId: phone.phoneId,
            phoneType: phone.phoneType,
            handsetIpei: phone.handsetIpei,
            baseStationHandsets: phone.baseStationHandsets?.map((baseStation) => {
               return {
                  baseStationHandsetId: baseStation.baseStationHandsetId,
                  deviceId: baseStation.deviceId,
                  phoneId: phone.phoneId,
                  sortId: baseStation.sortId,
                  fields: [],
               };
            }),
            numberOfLineKeys: phone.phoneType == 'Hardphone' ? phone.numberOfLineKeys : 1,
            sipUsers: phone.sipUsers?.map((sipUser) => {
               return {
                  accountId: sipUser.accountId,
                  fields: [],
                  hashPassword: sipUser.hashPassword,
                  sipUserId: sipUser.sipUserId,
                  userName: sipUser.userName,
               };
            }),
         };
      }),
      ringTimeout: extensionSave.extension.extensionPhone?.ringTimeOut,
      enableDtmf: extensionSave.extension.extensionPhone?.enableDtmf,
      isOtrEnabled: extensionSave.extension.extensionPhone?.isOtrEnabled,
      secondsToWait: extensionSave.extension.extensionPhone?.secondsToWait,
      dtmfToPlay: extensionSave.extension.extensionPhone?.dtmfToPlay,
      routeBusyExtensionId: extensionSave.extension.extensionPhone?.routeBusyExtensionId,
      routeBusyNullAction: extensionSave.extension.extensionPhone?.routeBusyNullAction,
      routeNoAnswerExtensionId: extensionSave.extension.extensionPhone?.routeNoAnswerExtensionId,
      routeNoAnswerNullAction: extensionSave.extension.extensionPhone?.routeNoAnswerNullAction,
      extensionSupervisees: extensionSave.extension.extensionPhone?.extensionSupervisees?.map((extensionSupervisee) => {
         return {
            supervisorExtensionId: extensionSave.extension.extensionId,
            fields: [],
            superviseeExtensionId: extensionSupervisee.superviseeExtensionId,
            extensionSuperviseeId: extensionSupervisee.extensionSuperviseeId,
         };
      }),
      extensionGroupSupervisees: extensionSave.extension.extensionPhone?.extensionGroupSupervisees?.map(
         (extensionGroupSupervisee) => {
            return {
               supervisorExtensionId: extensionSave.extension.extensionId,
               fields: [],
               extensionGroupSuperviseeId: extensionGroupSupervisee.extensionGroupSuperviseeId,
               superviseeExtensionGroupId: extensionGroupSupervisee.superviseeExtensionGroupId,
            };
         }
      ),
   };
}

export function createExtensionPagingGroupInput(extensionSave: ExtensionDetailSaveModel): ExtensionPagingGroupInput {
   return {
      extensionId: extensionSave.extension.extensionId,
      extensionPagingGroupId: extensionSave.extension.extensionPagingGroup?.extensionPagingGroupId,
      fields: [],
      pagingGroupMembers:
         extensionSave.extension.extensionPagingGroup?.pagingGroupMembers?.map((pgMember) => {
            return {
               extensionPagingGroupId: pgMember.extensionPagingGroupId,
               extensionId: pgMember.extensionId,
               fields: [],
               pagingGroupMemberId: pgMember.pagingGroupMemberId,
            };
         }) || [],
      pagingGroupExtensionGroups:
         extensionSave.extension.extensionPagingGroup?.pagingGroupExtensionGroups?.map((pgGroup) => {
            return {
               pagingGroupExtensionGroupId: pgGroup.pagingGroupExtensionGroupId || null,
               extensionPagingGroupId: pgGroup.extensionPagingGroupId,
               fields: [],
               extensionGroupId: pgGroup.extensionGroupId,
            };
         }) || [],
   };
}

export function createExtensionAutoAttendantInput(
   extensionSave: ExtensionDetailSaveModel
): ExtensionAutoAttendantInput {
   const autoAttendantInput: ExtensionAutoAttendantInput = {
      autoAttendantKeys: extensionSave.extension.extensionAutoAttendant?.autoAttendantKeys
         ?.filter((key) => {
            return !key.isDeleted;
         })
         .map((key) => {
            return {
               autoAttendantKeyId: key.autoAttendantKeyId,
               extensionAutoAttendantId: key.extensionAutoAttendantId,
               fields: [],
               keyNumber: key.keyNumber,
               routeTo: key.routeToExtension?.extensionId,
               voiceMailOnly: key.voiceMailOnly,
               routeToNullAction: key.routeToNullAction,
            };
         }),
      autoAttendantPrompts: extensionSave.extension.extensionAutoAttendant?.autoAttendantPrompts
         ?.filter((prompt) => {
            return !prompt.isDeleted;
         })
         .map((prompt) => {
            return {
               autoAttendantPromptId: prompt.autoAttendantPromptId,
               extensionAutoAttendantId: prompt.extensionAutoAttendantId,
               fields: [],
               promptId: prompt.prompt.promptId,
               sortId: prompt.sortId,
            };
         }),
      extensionAutoAttendantId: extensionSave.extension.extensionAutoAttendant?.extensionAutoAttendantId,
      extensionId: extensionSave.extension.extensionId,
      fields: [],
      timeoutExtensionId: extensionSave.extension.extensionAutoAttendant?.timeoutExtensionId ?? null,
      timeoutSeconds: extensionSave.extension.extensionAutoAttendant?.timeoutSeconds ?? null,
      firstPromptNotSkippable: extensionSave.extension.extensionAutoAttendant?.firstPromptNotSkippable ?? false,
      timeoutNullAction: extensionSave.extension.extensionAutoAttendant?.timeoutNullAction ?? NullActionTypes.Unset,
      isTimeoutVoicemailOnly: extensionSave.extension.extensionAutoAttendant?.isTimeoutVoicemailOnly ?? false,
   };

   return autoAttendantInput;
}

function createExtensionExternalForwardInput(extensionSave: ExtensionDetailSaveModel): ExtensionExternalForwardInput {
   const externalForwardInput: ExtensionExternalForwardInput = {
      dtmf: extensionSave.extension.extensionExternalForward?.dtmf,
      extensionExternalForwardId: extensionSave.extension.extensionExternalForward?.extensionExternalForwardId,
      extensionId: extensionSave.extension.extensionId,
      fields: [],
      forwardToNumber: extensionSave.extension.extensionExternalForward?.forwardToNumber,
      isDtmf: extensionSave.extension.extensionExternalForward?.isDtmf,
      isPassthroughOriginalCallerId: extensionSave.extension.extensionExternalForward?.isPassthroughOriginalCallerId,
      pauseSeconds: extensionSave.extension.extensionExternalForward?.pauseSeconds,
   };

   return externalForwardInput;
}

function createExtensionHuntGroupInput(extensionSave: ExtensionDetailSaveModel): ExtensionHuntGroupInput {
   const externalForwardInput: ExtensionHuntGroupInput = {
      callerIdOverride: extensionSave.extension.extensionHuntGroup?.callerIdOverride,
      displayName: extensionSave.extension.extensionHuntGroup?.displayName,
      extensionHuntGroupId: extensionSave.extension.extensionHuntGroup?.extensionHuntGroupId,
      extensionId: extensionSave.extension.extensionHuntGroup?.extensionId,
      fields: [],
      huntGroupMembers: extensionSave.extension.extensionHuntGroup?.huntGroupMembers?.map((hgMember) => {
         return {
            extensionHuntGroupId: hgMember.extensionHuntGroupId,
            extensionId: hgMember.extensionId,
            fields: [],
            huntGroupMemberId: hgMember.huntGroupMemberId,
         };
      }),
      extensionGroupHuntGroupMembers: extensionSave.extension.extensionHuntGroup?.extensionHuntGroups?.map(
         (hgMember) => {
            return {
               huntGroupExtensionGroupId: hgMember.huntGroupExtensionGroupId || null,
               huntGroupExtensionId: hgMember.huntGroupExtensionId,
               fields: [],
               extensionGroupId: hgMember.extensionGroupId,
            };
         }
      ),
      ringTimeout: extensionSave.extension.extensionHuntGroup?.ringTimeOut,
      routeBusyExtensionId: extensionSave.extension.extensionHuntGroup?.routeBusyExtensionId,
      routeBusyNullAction: extensionSave.extension.extensionHuntGroup?.routeBusyNullAction,
      routeNoAnswerExtensionId: extensionSave.extension.extensionHuntGroup?.routeNoAnswerExtensionId,
      isRouteBusyVoicemailOnly: extensionSave.extension.extensionHuntGroup?.isRouteBusyVoicemailOnly,
      isRouteNoAnswerVoicemailOnly: extensionSave.extension.extensionHuntGroup?.isRouteNoAnswerVoicemailOnly,
      routeNoAnswerNullAction: extensionSave.extension.extensionHuntGroup?.routeNoAnswerNullAction,
   };

   return externalForwardInput;
}

function createExtensionMailboxInput(extensionSave: ExtensionDetailSaveModel): ExtensionMailboxInput {
   const mailboxInput: ExtensionMailboxInput = {
      extensionId: extensionSave.extension.extensionId,
      extensionMailboxId: extensionSave.extension.extensionMailbox?.extensionMailboxId,
      fields: [],
   };

   return mailboxInput;
}

function createExtensionQueueInput(extensionSave: ExtensionDetailSaveModel): ExtensionQueueInput {
   const extensionQueueInput: ExtensionQueueInput = {
      announcementFrequency: extensionSave.extension.extensionQueue?.announcementFrequency,
      callersNeededForCallback: extensionSave.extension.extensionQueue?.callersNeededForCallback,
      callBackType: extensionSave.extension.extensionQueue?.callBackType,
      holdTimeNeededForCallback: extensionSave.extension.extensionQueue?.holdTimeNeededForCallback,
      customCallbackPromptId: extensionSave.extension.extensionQueue?.customCallbackPromptId,
      exitRouteTo: extensionSave.extension.extensionQueue?.exitRouteTo,
      exitRouteVoicemailOnly: extensionSave.extension.extensionQueue?.exitRouteVoicemailOnly,
      queueEmptyExitRouteTo: extensionSave.extension.extensionQueue?.queueEmptyExitRouteTo,
      queueEmptyExitNullAction: extensionSave.extension.extensionQueue?.queueEmptyExitNullAction,
      hasPreventEmptyQueue: extensionSave.extension.extensionQueue?.hasPreventEmptyQueue,
      extensionId: extensionSave.extension.extensionId,
      extensionQueueId: extensionSave.extension.extensionQueue?.extensionQueueId,
      fields: [],
      holdMusicPromptId: extensionSave.extension.extensionQueue?.holdMusicPromptId,
      isAnnounceQueueCount: extensionSave.extension.extensionQueue?.isAnnounceQueueCount,
      isAnnounceCallback: extensionSave.extension.extensionQueue?.isAnnounceCallback,
      isAnnounceWaitTime: extensionSave.extension.extensionQueue?.isAnnounceWaitTime,
      isCallBackAvailable: extensionSave.extension.extensionQueue?.isCallBackAvailable,
      periodicAnnouncementFrequency: extensionSave.extension.extensionQueue?.periodicAnnouncementFrequency,
      queueMembers: extensionSave.extension.extensionQueue?.queueMembers?.map((queueMember) => {
         return {
            agentExtensionId: queueMember.agentExtensionId,
            fields: [],
            extensionQueueId: queueMember.extensionQueueId,
            queueMemberId: queueMember.queueMemberId,
         };
      }),
      queueExtensionGroupMembers: extensionSave.extension.extensionQueue?.queueGroupMembers?.map((queueGroupMember) => {
         return {
            queueExtensionGroupId: queueGroupMember.queueExtensionGroupId,
            fields: [],
            extensionQueueId: queueGroupMember.extensionQueueId,
            extensionGroupId: queueGroupMember.extensionGroupId,
         };
      }),
      queuePeriodicAnnouncements: extensionSave.extension.extensionQueue?.queuePeriodicAnnouncements
         ?.filter((announcement) => {
            return !announcement.isDeleted;
         })
         .map((announcement) => {
            return {
               fields: [],
               promptId: announcement.prompt.promptId,
               queuePeriodicAnnouncementId: announcement.queuePeriodicAnnouncementId,
               sortOrder: announcement.sortOrder,
            };
         }),
      queueStrategyId: extensionSave.extension.extensionQueue?.queueStrategyId,
      wrapUpTime: extensionSave.extension.extensionQueue?.wrapUpTime,
   };

   return extensionQueueInput;
}

function createExtensionFaxInput(extensionSave: ExtensionDetailSaveModel): ExtensionFaxInput {
   return {
      defaultPhoneNumberId: extensionSave.extension.extensionFax?.defaultPhoneNumberId,
      emailAddress: extensionSave.extension.extensionFax?.emailAddress,
      extensionFaxId: extensionSave.extension.extensionFax?.extensionFaxId,
      extensionId: extensionSave.extension.extensionId,
      faxEmailAddresses: createFaxEmailAddresses(extensionSave.extension.extensionFax?.faxEmailAddresses ?? []),
      fields: [],
   };
}

function createExtensionFmFmInput(extensionSave: ExtensionDetailSaveModel): ExtensionFmFmInput {
   return {
      fields: [],
      fmFmSteps: extensionSave.extension.extensionFindMeFollowMe?.extensionFmFmSteps
         ?.filter((step) => !step.isDeleted)
         .map((step) => {
            return {
               fields: [],
               extensionFmFmId: step.extensionFmFmId,
               fmFmStepExtensions: step.fmFmStepExtensions
                  ?.filter((stepExtension) => !stepExtension.isDeleted)
                  .map((stepExtension) => {
                     return {
                        extensionId: stepExtension.extensionId,
                        fmFmStepExtensionId: stepExtension.fmFmStepExtensionId,
                        fmFmStepId: stepExtension.fmFmStepId,
                        fields: [],
                     };
                  }),
               fmFmStepPhoneNumbers: step.fmFmStepPhoneNumbers
                  ?.filter((stepPhoneNumber) => !stepPhoneNumber.isDeleted)
                  .map((stepPhoneNumber) => {
                     return {
                        fmFmStepPhoneNumberId: stepPhoneNumber.fmFmStepPhoneNumberId,
                        fmFmStepId: stepPhoneNumber.fmFmStepId,
                        phoneNumber: stepPhoneNumber.phoneNumber,
                        fields: [],
                     };
                  }),
               fmFmStepId: step.fmFmStepId,
               sequence: step.sequence,
               timeout: step.timeout,
            };
         }),
      extensionFmFmId: extensionSave.extension.extensionFindMeFollowMe?.extensionFmFmId,
      extensionId: extensionSave.extension.extensionFindMeFollowMe?.extensionId,
      finalDestinationExtensionId: extensionSave.extension.extensionFindMeFollowMe?.finalDestinationExtensionId,
      isFinalDestinationVoicemail: extensionSave.extension.extensionFindMeFollowMe?.isFinalDestinationVoicemail,
      isGreetingDisabled: extensionSave.extension.extensionFindMeFollowMe?.isGreetingDisabled,
      isIgnoreForward: extensionSave.extension.extensionFindMeFollowMe?.isIgnoreForward,
      isSendOriginalCallerId: extensionSave.extension.extensionFindMeFollowMe?.isSendOriginalCallerId,
      defaultPhoneNumberId: extensionSave.extension.extensionFindMeFollowMe?.defaultPhoneNumberId,
      finalDestinationPhoneNumber: extensionSave.extension.extensionFindMeFollowMe?.finalDestinationPhoneNumber,
   };
}

function createMailboxInput(extensionSave: ExtensionDetailSaveModel): MailboxInput {
   const mailboxInput: MailboxInput = {
      emailAddress: extensionSave.extension.mailbox?.emailAddress,
      discardAudioAfterEmail: extensionSave.extension.mailbox?.discardAudioAfterEmail,
      markReadAfterEmail: extensionSave.extension.mailbox?.markReadAfterEmail,
      sayCallerId: extensionSave.extension.mailbox?.sayCallerId,
      fields: [],
      isInNameDirectory: extensionSave.extension.mailbox?.isInNameDirectory,
      unavailablePromptId2: extensionSave.extension.mailbox?.unavailablePromptId2,
      temporaryUnavailablePromptId: extensionSave.extension.mailbox?.temporaryUnavailablePromptId,
      mailboxId: extensionSave.extension.mailbox?.mailboxId,
      namePromptId: extensionSave.extension.mailbox?.namePromptId,
      operatorExtensionId: extensionSave.extension.mailbox?.operatorExtensionId,
      password: extensionSave.extension.mailbox?.password,
      busyPromptId: extensionSave.extension.mailbox?.busyPromptId,
   };

   return mailboxInput;
}

export function createExtensionUserInput(
   currentExtensionUsers: ExtensionUserSaveModel[] | null,
   existingUsers: GetExtensionDetail_extension_usersConnection_items[] | null,
   extensionUsers: null | undefined | UserInput[]
): null | undefined | UserInput[] {
   const updatedExtensionUsers = extensionUsers;
   existingUsers
      ?.filter((user) => {
         return !currentExtensionUsers?.some((currentUsers) => {
            return currentUsers.userId === user.userId;
         });
      })
      .map((deleteUserExtension) => {
         updatedExtensionUsers?.push({
            extensionId: null,
            fields: [],
            userId: deleteUserExtension.userId,
         });
      });
   return updatedExtensionUsers;
}

type ExtensionEmbedFieldPhone = keyof ExtensionPhoneInput;
type ExtensionEmbedFieldAutoAttendant = keyof ExtensionAutoAttendantInput;
type ExtensionEmbedFieldExternalForward = keyof ExtensionExternalForwardInput;
type ExtensionEmbedFieldPagingGroup = keyof ExtensionPagingGroupInput;
type ExtensionEmbedFieldHuntGroup = keyof ExtensionHuntGroupInput;
type ExtensionEmbedFieldTimeBasedRoute = keyof TimeBasedRouteInput;
type ExtensionEmbedFieldMailbox = keyof ExtensionMailboxInput;
type ExtensionEmbedFieldQueue = keyof ExtensionQueueInput;
type ExtensionEmbedFieldFax = keyof ExtensionFaxInput;
type EmbedFieldMailbox = keyof MailboxInput;
type ExtensionEmbedFieldFindMeFollowMe = keyof ExtensionFmFmInput;

function formatExtensionEmbeddedPhoneFields(fields: string[], key: string, embedFields: ExtensionEmbedFieldPhone[]) {
   if (key) {
      for (const embedField of embedFields) {
         if (embedField === 'phones') {
            fields.push(
               ...phoneFields.map((keyField) => {
                  return `${key}.${embedField}.${keyField}`;
               })
            );
            const sipUserIndex = fields.indexOf(`${key}.${embedField}.sipUsers`);
            if (sipUserIndex > -1) {
               fields.splice(sipUserIndex, 1);
               fields.push(
                  ...sipUserFields.map((sipUserField) => {
                     return `${key}.${embedField}.sipUsers.${sipUserField}`;
                  })
               );
            }
            const deviceIndex = fields.indexOf(`${key}.${embedField}.devices`);
            if (deviceIndex > -1) {
               fields.splice(deviceIndex, 1);
               fields.push(
                  ...deviceFields.map((deviceField) => {
                     return `${key}.${embedField}.devices.${deviceField}`;
                  })
               );
            }
            const baseStationHandsetIndex = fields.indexOf(`${key}.${embedField}.baseStationHandsets`);
            if (baseStationHandsetIndex > -1) {
               fields.splice(baseStationHandsetIndex, 1);
               fields.push(
                  ...baseStationFields.map((handsetField) => {
                     return `${key}.${embedField}.baseStationHandsets.${handsetField}`;
                  })
               );
            }
         } else if (embedField === 'extensionGroupSupervisees') {
            fields.push(
               ...extensionGroupSuperviseeKeyFields.map((keyField) => {
                  return `${key}.${embedField}.${keyField}`;
               })
            );
         } else if (embedField === 'extensionSupervisees') {
            fields.push(
               ...extensionSuperviseeKeyFields.map((keyField) => {
                  return `${key}.${embedField}.${keyField}`;
               })
            );
         } else {
            fields.push(`${key}.${embedField}`);
         }
      }
   }
}

function formatExtensionEmbeddedAutoAttendantFields(
   fields: string[],
   key: string,
   embedFields: ExtensionEmbedFieldAutoAttendant[]
) {
   if (key) {
      for (const embedField of embedFields) {
         if (embedField === 'autoAttendantKeys') {
            fields.push(
               ...autoAttendantKeyFields.map((keyField) => {
                  return `${key}.${embedField}.${keyField}`;
               })
            );
         } else if (embedField === 'autoAttendantPrompts') {
            fields.push(
               ...autoAttendantPromptFields.map((promptField) => {
                  return `${key}.${embedField}.${promptField}`;
               })
            );
         } else {
            fields.push(`${key}.${embedField}`);
         }
      }
   }
}

function formatExtensionEmbeddedHuntGroupFields(
   fields: string[],
   key: string,
   embedFields: ExtensionEmbedFieldHuntGroup[]
) {
   if (key) {
      for (const embedField of embedFields) {
         if (embedField === 'huntGroupMembers') {
            fields.push(
               ...huntGroupMemberKeyFields.map((keyField) => {
                  return `${key}.${embedField}.${keyField}`;
               })
            );
         }
         if (embedField !== 'huntGroupMembers' && embedField !== 'extensionGroupHuntGroupMembers') {
            fields.push(`${key}.${embedField}`);
         }
      }
   }
}

function formatExtensionEmbeddedPagingGroupFields(
   fields: string[],
   key: string,
   embedFields: ExtensionEmbedFieldPagingGroup[]
) {
   if (key) {
      for (const embedField of embedFields) {
         if (embedField === 'pagingGroupMembers') {
            fields.push(
               ...pagingGroupMemberKeyFields.map((keyField) => {
                  return `${key}.${embedField}.${keyField}`;
               })
            );
         }
         if (embedField !== 'pagingGroupMembers' && embedField !== 'pagingGroupExtensionGroups') {
            fields.push(`${key}.${embedField}`);
         }
      }
   }
}

function formatExtensionGroupEmbeddedHuntGroupFields(
   fields: string[],
   key: string,
   embedFields: ExtensionEmbedFieldHuntGroup[]
) {
   if (key) {
      const extensionGroupMemberKeyFields: string[] = [
         'huntGroupExtensionGroupId',
         'huntGroupExtensionId',
         'extensionGroupId',
      ];
      for (const embedField of embedFields) {
         if (embedField === 'extensionGroupHuntGroupMembers') {
            fields.push(
               ...extensionGroupMemberKeyFields.map((keyField) => {
                  return `${key}.extensionGroupHuntGroupMembers.${keyField}`;
               })
            );
         }
      }
   }
}

function formatExtensionGroupEmbeddedPagingGroupFields(
   fields: string[],
   key: string,
   embedFields: ExtensionEmbedFieldPagingGroup[]
) {
   if (key) {
      const extensionGroupMemberKeyFields: string[] = [
         'pagingGroupExtensionGroupId',
         'extensionPagingGroupId',
         'extensionGroupId',
      ];
      for (const embedField of embedFields) {
         if (embedField === 'pagingGroupExtensionGroups') {
            fields.push(
               ...extensionGroupMemberKeyFields.map((keyField) => {
                  return `${key}.pagingGroupExtensionGroups.${keyField}`;
               })
            );
         }
      }
   }
}

function formatExtensionGroupEmbeddedQueueGroupFields(
   fields: string[],
   key: string,
   embedFields: ExtensionEmbedFieldQueue[]
) {
   if (key) {
      const extensionGroupMemberKeyFields: string[] = ['queueExtensionGroupId', 'extensionGroupId', 'extensionQueueId'];
      for (const embedField of embedFields) {
         if (embedField === 'queueExtensionGroupMembers') {
            fields.push(
               ...extensionGroupMemberKeyFields.map((keyField) => {
                  return `${key}.queueExtensionGroupMembers.${keyField}`;
               })
            );
         }
      }
   }
}

function formatExtensionEmbeddedExternalForwardFields(
   fields: string[],
   key: string,
   embedFields: ExtensionEmbedFieldExternalForward[]
) {
   if (key) {
      for (const embedField of embedFields) {
         fields.push(`${key}.${embedField}`);
      }
   }
}

function formatTimeBasedRouteFields(fields: string[], key: string, embedFields: ExtensionEmbedFieldTimeBasedRoute[]) {
   if (key) {
      for (const embedField of embedFields) {
         fields.push(`${key}.${embedField}`);
      }
   }
}

function formatExtensionEmbeddedMailboxFields(
   fields: string[],
   key: string,
   embedFields: ExtensionEmbedFieldMailbox[]
) {
   if (key) {
      for (const embedField of embedFields) {
         fields.push(`${key}.${embedField}`);
      }
   }
}

function formatExtensionEmbeddedQueueFields(fields: string[], key: string, embedFields: ExtensionEmbedFieldQueue[]) {
   if (key) {
      for (const embedField of embedFields) {
         if (embedField === 'queueMembers') {
            fields.push(
               ...queueMemberKeyFields.map((keyField) => {
                  return `${key}.${embedField}.${keyField}`;
               })
            );
         } else if (embedField === 'queuePeriodicAnnouncements') {
            fields.push(
               ...queuePeriodicAnnouncementFields.map((promptField) => {
                  return `${key}.${embedField}.${promptField}`;
               })
            );
         } else {
            if (embedField !== 'queueExtensionGroupMembers') fields.push(`${key}.${embedField}`);
         }
      }
   }
}

function formatExtensionEmbeddedFaxFields(fields: string[], key: string, embedFields: ExtensionEmbedFieldFax[]) {
   if (key) {
      for (const embedField of embedFields) {
         fields.push(`${key}.${embedField}`);
      }
   }
}

function formatExtensionEmbeddedFaxFieldsForFaxEmailAddresses(fields: string[]) {
   const embedFields = [
      'extensionFax.faxEmailAddresses.emailAddress',
      'extensionFax.faxEmailAddresses.extensionId',
      'extensionFax.faxEmailAddresses.faxEmailAddressId',
      'extensionFax.faxEmailAddresses.isDomainOnly',
   ];
   for (const embedField of embedFields) {
      fields.push(embedField);
   }
}

function formatExtensionEmbeddedFindMeFollowMeFields(
   fields: string[],
   key: string,
   embedFields: ExtensionEmbedFieldFindMeFollowMe[]
) {
   if (key) {
      for (const embedField of embedFields) {
         if (embedField === 'fmFmSteps') {
            for (const findMeFollowMeStepKeyField of findMeFollowMeStepKeyFields) {
               if (findMeFollowMeStepKeyField === 'fmFmStepExtensions') {
                  fields.push(
                     ...findMeFollowMeStepExtensionsKeyFields.map((extKeyField) => {
                        return `${key}.${embedField}.${findMeFollowMeStepKeyField}.${extKeyField}`;
                     })
                  );
               } else if (findMeFollowMeStepKeyField === 'fmFmStepPhoneNumbers') {
                  fields.push(
                     ...findMeFollowMeStepPhoneNumbersKeyFields.map((phoneNumberKeyField) => {
                        return `${key}.${embedField}.${findMeFollowMeStepKeyField}.${phoneNumberKeyField}`;
                     })
                  );
               } else {
                  fields.push(`${key}.${embedField}.${findMeFollowMeStepKeyField}`);
               }
            }
         } else {
            fields.push(`${key}.${embedField}`);
         }
      }
   }
}

function formatEmbeddedMailboxFields(fields: string[], key: string, embedFields: EmbedFieldMailbox[]) {
   if (key) {
      for (const embedField of embedFields) {
         fields.push(`${key}.${embedField}`);
      }
   }
}

function formatEmbeddedUserFields(fields: string[], key: string) {
   if (key) {
      fields.push(
         ...extensionUserKeyFields.map((keyField) => {
            return `${key}.${keyField}`;
         })
      );
   }
}

function formatEmbeddedFaxEmailAddressFields(fields: string[], key: string) {
   if (key) {
      fields.push(
         ...faxEmailAddressesFields.map((keyField) => {
            return `${key}.${keyField}`;
         })
      );
   }
}

function formatEmbeddedDepartmentExtensionFields(fields: string[], key: string, isSavingNewDepartments: boolean) {
   if (key) {
      for (const departmentExtensionKeyField of departmentExtensionKeyFields) {
         if (departmentExtensionKeyField === 'department') {
            if (isSavingNewDepartments) {
               for (const deptField of departmentKeyFields) {
                  fields.push(`${key}.${departmentExtensionKeyField}.${deptField}`);
               }
            }
         } else {
            fields.push(`${key}.${departmentExtensionKeyField}`);
         }
      }
   }
}
function createFaxEmailAddresses(faxEmailAddresses): FaxEmailAddressInput[] {
   if (!faxEmailAddresses) return [];

   return faxEmailAddresses
      .filter((fax) => fax.isDeleted === false)
      .map((fax) => ({
         faxEmailAddressId: fax.faxEmailAddressId ?? 0,
         emailAddress: fax.emailAddress ?? '',
         isDomainOnly: false,
         extensionId: fax.extensionId ?? 0,
         fields: [],
      }));
}

export type ExtensionSaveResult = ExecutionResult<SaveExtension>;
